import { CITATION_LINK_PREFIX } from './constants';

const SCHEME_PREFIX = /^[a-z][a-z0-9+.-]*:/i;
const HTTP_SCHEME = /^https?:/i;
/** `/p/:role/:page` or `/dp/:role/:page`, optionally under a basename. */
const PORTAL_PAGE_SUFFIX = /\/(?:p|dp)\/[^/]+\/[^/]+/;

function portalPagePath(pathname: string): string | null {
  const match = PORTAL_PAGE_SUFFIX.exec(pathname);
  if (!match) {
    return null;
  }
  return pathname.slice(pathname.indexOf(match[0]));
}

function stripBasename(pathname: string, basename: string): string {
  const base = basename.replace(/\/+$/, '');
  if (!base) {
    return pathname;
  }
  if (pathname === base || pathname.startsWith(`${base}/`)) {
    return pathname.slice(base.length) || '/';
  }
  return pathname;
}

/**
 * Whether a markdown href should be routed inside the portal instead of a
 * full document load. Relative paths and same-origin URLs qualify. Cross-origin
 * URLs qualify only when the path is a portal page (`/p/:role/:page` or
 * `/dp/:role/:page`), so Medium-style `/p/slug` links stay native. Fragments,
 * citations, and other schemes stay native.
 */
export function isInAppAssistantHref(href: string | undefined): boolean {
  if (href == null || href === '' || href.startsWith('#')) {
    return false;
  }
  if (href.startsWith(CITATION_LINK_PREFIX)) {
    return false;
  }
  if (SCHEME_PREFIX.test(href) && !HTTP_SCHEME.test(href)) {
    return false;
  }

  try {
    const url = new URL(href, globalThis.window.location.href);
    return (
      url.origin === globalThis.window.location.origin ||
      portalPagePath(url.pathname) != null
    );
  } catch {
    return false;
  }
}

/**
 * Turns an in-app assistant href into a React Router location.
 *
 * Normalization pipeline (apply once with as much context as you have):
 * 1. Drop origin.
 * 2. For portal page paths (`/p/...`, `/dp/...`), drop any prefix before that
 *    suffix so `/next/p/role/funds` and `https://host/next/p/role/funds` both
 *    become `/p/role/funds`.
 * 3. When `basename` is provided (app shell), strip that prefix so remaining
 *    same-origin paths like `/next/dashboard` become `/dashboard`.
 *
 * portal-ui link interception and source dedupe typically use steps 1–2 only.
 * portal-next `onNavigate` should pass `ROUTE_BASE_NAME` so step 3 runs before
 * `navigate`.
 */
export function toInAppAssistantLocation(href: string, basename = ''): string {
  const url = new URL(href, globalThis.window.location.href);
  const pathname = stripBasename(
    portalPagePath(url.pathname) ?? url.pathname,
    basename
  );
  return `${pathname}${url.search}${url.hash}`;
}
